/**
 * Represents a service message returned from a Nu Skin REST API.
 * Service messages include both localized and technical error text.
 * Localized error message text is intended to be displayed to the user,
 * while technical error message text is intended as a debugging aid.
 * @property {string} level - Message level: "INFO", "WARN", "ERROR".
 * @property {string} code - Error code reported by the server.
 * @property {string} type - Exception type thrown by the server.
 * @property {string} message - Localized error message text.
 * @property {string} technicalMessage - Technical error message text.
 */
export class ServiceMessage {

    /**
     * ServiceMessage constructor.
     * @param {Object} data - Nu Skin REST API service message raw json object.
     */
    constructor(data = {}) {
        this.level = data.level;
        this.code = data.code;
        this.type = data.type;
        this.message = data.message;
        this.technicalMessage = data.technicalMessage;
    }
}

/**
 * Thrown if an error response is received from a REST API or other online resource.
 */
export class NetworkError extends Error {
    /**
     * NetworkError constructor.
     * @param {string} [message] - A human-readable description of the error, for debugging purposes.
     */
    constructor(message) {
        super(message);
        this.constructor = NetworkError;
        this.__proto__ = NetworkError.prototype;
        this.name = this.constructor.name;
    }
}

/**
 * Thrown if a 404 "Not Found" response is received from a REST API
 * or other online resource.
 */
export class ResourceNotFoundError extends NetworkError {

    /**
     * ResourceNotFoundError constructor.
     * @param {string} [message] - A human-readable description of the error, for debugging purposes.
     */
    constructor(message) {
        super(message);
        this.constructor = ResourceNotFoundError;
        this.__proto__ = ResourceNotFoundError.prototype;
        this.name = this.constructor.name;
    }
}

/**
 * Thrown if an error response (400- or 500-series status code)
 * is received from a Nu Skin REST API endpoint.
 * NsRestError exposes the response's http status code
 * and list of service message objects.
 * @property {number} status - Http status code.
 * @property {Array.<ServiceMessage>} messages - List of service message objects.
 * @see ServiceMessage
 */
export class NsRestError extends NetworkError {

    /**
     * NsRestError constructor.
     *
     * If instantiating NsRestError from a REST API response, use {@link NsRestError.fromRestResponse}.
     *
     * @param {number} status - Http status code.
     * @param {Array.<ServiceMessage>} [messages] - List of service message objects.
     * @param {string} [debugMsg] - A human-readable description of the error, for debugging purposes.
     * This is in addition to the list of service messages.
     */
    constructor(status, messages, debugMsg) {
        super(debugMsg);
        this.constructor = NsRestError;
        this.__proto__ = NsRestError.prototype;
        this.name = this.constructor.name;

        this.status = status;
        this.messages = (messages || []).map(m => new ServiceMessage(m));
    }

    /**
     * Transforms a Nu Skin REST API response body into an instance of NsRestError.
     * @param {Object} response - REST API json response object
     * @param {string} [debugMsg] - A human-readable description of the error, for debugging purposes.
     * This is in addition to any service messages included in the response object.
     */
    static fromRestResponse(response, debugMsg) {
        return new NsRestError(response['status'], response['messages'], debugMsg);
    }
}

/**
 * Thrown whenever a service requires authentication credentials for
 * the current user and cannot find them.
 * This may be caused by an unauthenticated user attempting an action
 * that requires authentication.
 */
export class NsMissingCredentialsError extends Error {

    /**
     * NsMissingCredentialsError constructor.
     * @param {string} [message] - A human-readable description of the error
     */
    constructor(message) {
        super(message);
        this.constructor = NsMissingCredentialsError;
        this.__proto__ = NsMissingCredentialsError.prototype;
        this.name = this.constructor.name;
    }
}

/**
 * This will be thrown when we attempt to add something to the cart
 * that is marked as an exclusive offer, and the user is not qualified to purchase
 */
export class NsNotQualifiedException extends Error {

    /**
     * NsNotQualifiedException constructor.
     * @param {string} [message] - The message that the end user would see (Human readable)
     */
    constructor(message) {
        super(message);
        this.constructor = NsNotQualifiedException;
        this.__proto__ = NsNotQualifiedException.prototype;
        this.name = this.constructor.name;
    }
}

/**
 * This is thrown when the requested quantity for a cart item
 * is not available.
 */
export class NsQuantityNotAvailable extends Error {

    /**
     * NsQuantityNotAvailable constructor.
     * @param {string} [message] - The message that the end user would see (Human readable)
     */
    constructor(message) {
        super(message);
        this.constructor = NsQuantityNotAvailable;
        this.__proto__ = NsQuantityNotAvailable.prototype;
        this.name = this.constructor.name;
    }
}

/**
 * This is thrown when the product status is not RELEASED_FOR_SALE
 */
export class NsProductNotReleasedForSale extends Error {

    /**
     * NsProductNotReleasedForSale constructor.
     * @param {string} [message] - The message that the end user would see (Human readable)
     */
    constructor(message) {
        super(message);
        this.constructor = NsProductNotReleasedForSale;
        this.__proto__ = NsProductNotReleasedForSale.prototype;
        this.name = this.constructor.name;
    }
}
